import React, { createContext, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Dispatch, SetStateAction } from 'react';
import { useProductOptions } from '../../../components/BriefForm/FormFields/ClientConfiguredFields/ProductOptions';
import { useBriefContext } from '../../BriefContext';
import { useUpdateBriefMutation } from '../brief.generated';
import { transformBriefFormData } from '../getSubmit';
import type { FieldGroupData } from '../FieldGroup';

interface EditModalContextValue {
    isOpen: boolean;
    isSaving: boolean;
    open: () => void;
    cancel: () => Promise<void>;
    close: () => Promise<void>;
}

const EditModalContext = createContext<EditModalContextValue>({
    isOpen: false,
    isSaving: false,
    open: () => null,
    cancel: async () => undefined,
    close: async () => undefined,
});

export function useEditModal() {
    return useContext(EditModalContext);
}

export function EditFormGroupModalProvider({
    children,
    group,
    setGroupFormKey,
    briefId,
}: {
    children: React.ReactNode;
    group: FieldGroupData;
    setGroupFormKey: Dispatch<SetStateAction<number>>;
    briefId: string;
}) {
    const { isOpen, isSaving, open, cancel, close } = useEditState(group, setGroupFormKey, briefId);

    return (
        <EditModalContext.Provider value={{ isOpen, isSaving, open, cancel, close }}>
            {children}
        </EditModalContext.Provider>
    );
}

export function useEditState(
    group: FieldGroupData,
    setGroupFormKey: Dispatch<SetStateAction<number>>,
    briefId: string
) {
    const [isOpen, setIsOpen] = useState(false);
    const form = useFormContext();
    const [savedFormValue, setSavedFormValue] = useState(form.getValues());
    const setError = form?.setError;
    const { updateProduct, product } = useBriefContext();
    const [savedProduct, setSavedProduct] = useState(product);
    const { selectedOptions } = useProductOptions();
    const [savedProductOptions, setSavedProductOptions] = useState<Record<string, string>>();

    useEffect(() => {
        if (isOpen) {
            // Intentionally snapshot values at the moment the modal opens, not on every change
            // eslint-disable-next-line react-hooks/exhaustive-deps
            setSavedProductOptions(selectedOptions);
            setSavedFormValue(form.getValues());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const [updateBrief, { loading }] = useUpdateBriefMutation({
        update: (cache, { data }) => {
            if (!data) {
                return;
            }
            if (data.brief.errors) {
                data.brief.errors.forEach((error) => {
                    setError(error.field, { type: 'server', message: error.reason });
                });
            }
            if (data.brief.dynamicFields) {
                cache.modify({
                    id: cache.identify(data.brief),
                    fields: {
                        dynamicFields: () => data.brief.dynamicFields,
                    },
                });
            }
        },
    });

    const onSubmit = () => {
        updateProduct(savedProduct);
        return updateBrief({
            variables: {
                id: briefId,
                fields: {
                    ...transformBriefFormData({
                        ...savedFormValue,
                        productOptions: savedProductOptions,
                    }),
                },
            },
        });
    };

    const open = () => {
        setIsOpen(true);
    };

    const cancel = async () => {
        await onSubmit();

        Object.keys(savedFormValue).forEach((key) => {
            const value = savedFormValue[key];
            form.setValue(key, value);
        });

        // sleep for 0.3 seconds to address bug in POUI, see: https://vistaprint.slack.com/archives/C057FQY14CT/p1740448662286579
        if (savedProductOptions) {
            const isCustomSizeChanged =
                savedProductOptions['Custom Height'] !== selectedOptions['Custom Height'] ||
                savedProductOptions['Custom Width'] !== selectedOptions['Custom Width'];

            if (isCustomSizeChanged) {
                await new Promise((resolve) => setTimeout(resolve, 300));
            }
        }

        setGroupFormKey((prev) => prev + 1);

        setIsOpen(false);
    };

    const close = async () => {
        const isValid = await form.trigger(group.fields.map((f) => f.id));
        if (!isValid) {
            return;
        }

        setIsOpen(false);
        setSavedProduct(product);
    };
    return { isOpen, isSaving: loading, open, cancel, close };
}
